/**
 * Mesh / Peer-to-Peer topology scene — Coworking Grid.
 *
 * Campus layout:
 *   - Each agent gets a 4x4 building
 *   - Arranged in a grid (3 columns)
 *   - Stone paths connect all horizontal and vertical neighbors
 *
 * Scales: more agents = bigger grid.
 */

import type { SceneLayout, TopologyInfo, BuildingFootprint, CampusDef } from "./SceneLayout.js";
import { campusToLayout, routePath } from "./SceneLayout.js";
import { getFurnitureForBuilding } from "./FurniturePresets.js";

const BUILDING_SIZE = 4;
const GAP = 4;
const COLS = 3;

export function meshLayout(topo: TopologyInfo): SceneLayout {
  const count = topo.agents.length;
  const buildings: BuildingFootprint[] = [];

  for (let i = 0; i < count; i++) {
    const gridR = Math.floor(i / COLS);
    const gridC = i % COLS;
    const bCol = gridC * (BUILDING_SIZE + GAP);
    const bRow = gridR * (BUILDING_SIZE + GAP);

    buildings.push({
      id: `agent-${i}`,
      col: bCol,
      row: bRow,
      width: BUILDING_SIZE,
      height: BUILDING_SIZE,
      floorType: "office",
      agents: [{
        agentId: topo.agents[i].id,
        localCol: 2,
        localRow: 2,
        deskType: "small",
        isEntryPoint: topo.agents[i].id === topo.entryPoint,
      }],
      furniture: getFurnitureForBuilding(
        topo.agents[i].id === topo.entryPoint ? "hub" : "worker",
        "coworking",
        bCol, bRow, BUILDING_SIZE, BUILDING_SIZE,
      ),
    });
  }

  // Route paths between horizontal and vertical neighbors
  const pathCells: Array<{ col: number; row: number }> = [];
  for (let i = 0; i < count; i++) {
    const gridC = i % COLS;

    // Right neighbor
    if (gridC < COLS - 1 && i + 1 < count) {
      pathCells.push(...routePath(buildings[i], buildings[i + 1]));
    }

    // Below neighbor
    if (i + COLS < count) {
      pathCells.push(...routePath(buildings[i], buildings[i + COLS]));
    }
  }

  const campus: CampusDef = {
    buildings,
    pathCells,
    decorations: [],
  };

  return campusToLayout(campus);
}
